import { useState, useMemo } from "react";
import { trackEvent } from "@/hooks/useEventTracking";
import { shelterFields } from "@/lib/analyticsEvents";
import { useNavigate } from "react-router-dom";
import { Search, PlusCircle, MapPin, BadgeCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { Shelter } from "@/types/shelter";
import { normalizeText } from "@/lib/geoText";

interface ShelterSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shelters: Shelter[];
  onShelterSelect?: (shelter: Shelter) => void;
}

export const ShelterSearchDialog = ({ open, onOpenChange, shelters, onShelterSelect }: ShelterSearchDialogProps) => {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const results = useMemo(() => {
    const q = normalizeText(query);
    if (!q || q.length < 2) return [];
    return shelters
      .filter(s => normalizeText(s.name).includes(q) || normalizeText(s.city).includes(q))
      .slice(0, 8);
  }, [query, shelters]);

  const reset = () => setQuery("");

  const handlePick = (shelter: Shelter) => {
    trackEvent("Otworzono schronisko", { ...shelterFields(shelter), source: "hero-search" });
    onOpenChange(false);
    reset();
    onShelterSelect?.(shelter);
  };

  const handleAddNew = () => {
    onOpenChange(false);
    reset();
    navigate("/dodaj-schronisko");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Znajdź swoje schronisko</DialogTitle>
          <DialogDescription>
            Wpisz nazwę lub miasto. Po kliknięciu otworzymy profil - na karcie znajdziesz przycisk do przejęcia.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Nazwa schroniska lub miasto..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="pl-9"
            autoFocus
          />
        </div>

        {query.length >= 2 && (
          <ScrollArea className="max-h-60">
            {results.length > 0 ? (
              <div className="space-y-1">
                {results.map(s => (
                  <button
                    key={s.id}
                    onClick={() => handlePick(s)}
                    className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-accent transition-colors"
                  >
                    <span className="font-semibold text-sm text-foreground flex items-center gap-1.5">
                      {s.name}
                      {s.organization_verified && (
                        <BadgeCheck className="w-3.5 h-3.5 text-primary shrink-0" />
                      )}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                      <MapPin className="w-3 h-3" />
                      {s.city}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                Nie znaleziono schroniska
              </p>
            )}
          </ScrollArea>
        )}

        <Button variant="outline" className="w-full" onClick={handleAddNew}>
          <PlusCircle className="w-4 h-4" />
          Dodaj nowe schronisko
        </Button>
      </DialogContent>
    </Dialog>
  );
};
